import { BsModalRef } from 'ngx-bootstrap/modal';
import { Component, OnDestroy, OnInit, TemplateRef, AfterViewChecked, HostListener } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';

import { BasePrintEmailComponent } from './../shared/base-print-email.component';
import { CartService } from './../cart/cart.service';
import { asyncPattern, asyncRequired } from '../shared/field-validators';

@Component({
  selector: 'app-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.less', './../shared/print-email.component.less']
})
export class EmailComponent extends BasePrintEmailComponent implements OnInit, OnDestroy, AfterViewChecked {
    submitted = false;
    emailFormGroup: FormGroup;
    public previewPdfModalRef: BsModalRef;

     // validation parameters and validators
    delayTime = 1000; // ms
    emailRegExp = new RegExp(/^\s*(.{1,}@([\-a-z0-9A-Z]+\.)+[a-z0-9A-Z]+)\s*$/, 'i');
    emailValidators = [Validators.required, Validators.pattern(this.emailRegExp)];
    emailAsyncValidators = [asyncRequired(0), asyncPattern(this.emailRegExp, this.delayTime)];

    ngOnInit() {
        this.currentStep = 1;
        this.errors = [];
        this.submitted = false;

        this.emailFormGroup = this.formBuilder.group({
            emailAddress: ['', [Validators.required], this.emailAsyncValidators]
      });
    }

    /**
     * resize the email confirm list table after view being checked
     */
    ngAfterViewChecked() {
        this.resizeConfirmTableHeight();
    }

    /**
     * resize the email confirm list table upone the window resize event
     * @param
     */
    @HostListener('window:resize', ['$event'])
    onResize($event) {
      this.resizeConfirmTableHeight();
    }

    getValidEmailForDisplay(): string {
        const email = this.emailFormGroup.get('emailAddress');
        return (email.dirty && email.valid) ? email.value : '';
    }

    preview(previewTemplate: TemplateRef<any>): void {
      this.generatePdf();
      this.previewPdfModalRef = this.modalService.show(previewTemplate, {class: 'modal-lg'});
    }

    gotoEmailStep(): void {
       this.eventService.focusField('civil-email-address');
       this.errors = [];
       this.currentStep = 2;
    }

    verifyAndSendEmail(): void {
        // validation is pending, return
        if (this.emailFormGroup.pending) {
            return;
        }

        // TODO: debate if the above code should execute when submitting an invalid form
        this.errors = [];
        this.submitted = true;

        if (this.emailFormGroup.invalid) {
            return;
        }

        const emailAddress = this.emailFormGroup.get('emailAddress').value;

        this.requestSubscription = this.printService
                                       .requestEmail(this.cases, emailAddress)
                                       .subscribe(
                                         (wrappedResponse) => {
                                            this.submitted = false;
                                            this.currentStep    = 3;
                                        },
                                        (errorMessage: string) => {
                                            this.errors.push(errorMessage);
                                            this.submitted = false;
                                        }
                                       );
    }

    isEmailRequestInProgress(): boolean {
        return this.requestSubscription && !this.requestSubscription.closed;
    }

    ngOnDestroy() {
        if (this.previewPdfModalRef) {
          this.previewPdfModalRef.hide();
        }
    }

    /**
     * resize the confirm list table height, so that confirm button in the modal
     * does not extend outside the window/viewport
     */
    resizeConfirmTableHeight() {
        if (this.currentStep === 1) {
          const  TOP_HEIGHT = 195;    // px;  -- height above the confirm list table
          const  BOTTOM_HEIGHT = 170; // px;  -- height under the confirm list  table (though not fixed for different window size)
          const  TABLE_HEIGHT_WITH_ONE_ROW = 58; // px; -- table height with one row

          // window and table height
          let windowHeight: number = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
          let tableWrapperDiv = document.getElementById('civil-email-table-wrapper-div') as HTMLElement; // email confirm list table
          let tableScrollHeight = tableWrapperDiv.scrollHeight;

          // modal size
          let modalSizeCalculated = tableScrollHeight + TOP_HEIGHT + BOTTOM_HEIGHT;

          // resize the confirm list table and keep at least one row in modal
          if (tableScrollHeight > TABLE_HEIGHT_WITH_ONE_ROW && modalSizeCalculated > windowHeight  ) {
               let heightDiff  =  modalSizeCalculated - windowHeight;
               let tableExpectedHeight =  tableScrollHeight - heightDiff;
               if (tableExpectedHeight >= TABLE_HEIGHT_WITH_ONE_ROW) {
                  tableWrapperDiv.style.height = tableExpectedHeight + 'px';
                  tableWrapperDiv.style.overflowY = 'auto';
               } else {
                  tableWrapperDiv.style.height = TABLE_HEIGHT_WITH_ONE_ROW + 'px';
                  tableWrapperDiv.style.overflowY = 'auto';
               }
           } else {
              tableWrapperDiv.style.height = tableScrollHeight + 'px';
           }
        }
      }


  /**
   * Due to Angular validation has less access points for setting validators for different event in
   * the validator configuration (still seeking the solution in this way),  Meanwhile, just reset
   * validators and update validity for the form controls as well as the form group (if necessary)
   * to achieve the validation requirement specifications.
   *
   * @param $event Event
   * @param formControlName: string
   * @param formName: string
   */
  updateValidity($event, formControlName: string, formName: string) {
    if ($event.type === 'blur') {
      // on blur - instant validation
      this.emailFormGroup.get(formControlName).clearAsyncValidators();
      this.emailFormGroup.get(formControlName).setValidators(this.emailValidators);
      this.emailFormGroup.get(formControlName).updateValueAndValidity();
    } else if ($event.type === 'focus') {
      // on focus -instant validation if initial value entry exists upon focus, else asychrous validation
      if (this.emailFormGroup.get(formControlName).value !== null
          && this.emailFormGroup.get(formControlName).value !== undefined
          && this.emailFormGroup.get(formControlName).value.length > 0 ) {
        this.emailFormGroup.get(formControlName).clearAsyncValidators();
        this.emailFormGroup.get(formControlName).setValidators(this.emailValidators);
        this.emailFormGroup.get(formControlName).updateValueAndValidity();
      } else {
        this.emailFormGroup.get(formControlName).clearValidators();
        this.emailFormGroup.get(formControlName).setValidators([Validators.required]);
        this.emailFormGroup.get(formControlName).setAsyncValidators(this.emailAsyncValidators);
        this.emailFormGroup.get(formControlName).updateValueAndValidity();
      }
    } else if ($event.type === 'keypress') {
      // key press - instant validation for enter key;
      //             instant validation for initial value exists and invalid status upon key press;
      //             asynchrous validation for initial value empty or valid status upon key press
      //                                     (stauts may be continuously updated in asynchrous validation)
      let keyCode = (typeof $event.which === 'number') ? $event.which : $event.keyCode;
      if (keyCode === 13) {  // key press - enter key
        this.emailFormGroup.get(formControlName).clearAsyncValidators();
        this.emailFormGroup.get(formControlName).setValidators(this.emailValidators);
        this.emailFormGroup.get(formControlName).updateValueAndValidity();
      } else {                // key press - any other key
        if (this.emailFormGroup.get(formControlName).value !== null
            && this.emailFormGroup.get(formControlName).value !== undefined
            && this.emailFormGroup.get(formControlName).value.length > 1
            && this.emailFormGroup.get(formControlName).invalid) {
          this.emailFormGroup.get(formControlName).clearAsyncValidators();
          this.emailFormGroup.get(formControlName).setValidators(this.emailValidators);
          this.emailFormGroup.get(formControlName).updateValueAndValidity();
        } else {
          this.emailFormGroup.get(formControlName).clearValidators();
          this.emailFormGroup.get(formControlName).setValidators([Validators.required]);
          this.emailFormGroup.get(formControlName).setAsyncValidators(this.emailAsyncValidators);
          this.emailFormGroup.get(formControlName).updateValueAndValidity();
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/pipeline-ciprs-ui-final/project/src/app/email/email.component.ts